/**
 * @file InfoTip — a small ⓘ button that reveals a plain-language description on
 * hover (desktop), focus (keyboard) or tap (mobile). Used in the sidebar so a
 * no-code user can learn what each area does without leaving the menu.
 *
 * The popover is `position: fixed`, anchored to the icon's bounding rect, so it
 * escapes the sidebar's `overflow: auto` clipping.
 */

import { useState, useRef } from "react";
import { Info } from "lucide-react";

export function InfoTip({ text, label }: { text: string; label: string }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const ref = useRef<HTMLButtonElement>(null);

  const place = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setCoords({ top: r.top + r.height / 2, left: r.right + 10 });
  };
  const show = () => {
    place();
    setOpen(true);
  };
  const hide = () => setOpen(false);

  return (
    <>
      <button
        ref={ref}
        type="button"
        aria-label={`Co to jest: ${label}`}
        className="flex h-5 w-5 items-center justify-center rounded text-gray-500 transition-colors hover:text-accent focus:text-accent focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          // Don't let the click navigate the surrounding nav link.
          e.preventDefault();
          e.stopPropagation();
          if (open) hide();
          else show();
        }}
      >
        <Info className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open && (
        <span
          role="tooltip"
          className="pointer-events-none fixed z-[60] w-64 -translate-y-1/2 rounded-lg border border-border bg-surface-2 px-3 py-2 text-xs leading-relaxed text-gray-200 shadow-xl"
          style={{ top: coords.top, left: coords.left }}
        >
          {text}
        </span>
      )}
    </>
  );
}
